/**
 * Cohort Retention Matrix Component
 *
 * Shows weekly cohort retention heatmap
 */

import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LockedChartCard } from "./locked-chart-card";
import { MetricTooltip } from "./metric-tooltip";

interface CohortRetentionMatrixProps {
  data: Array<{
    cohortWeek: string;
    cohortStartDate: string;
    cohortSize: number;
    weeklyRetention: Array<{
      weekNumber: number;
      customersReturned: number;
      retentionPercentage: number;
    }>;
  }> | null;
}

// Get color based on retention percentage
function getRetentionColor(percentage: number): string {
  if (percentage >= 60) return "bg-green-500";
  if (percentage >= 40) return "bg-green-400";
  if (percentage >= 25) return "bg-yellow-400";
  if (percentage >= 15) return "bg-orange-400";
  if (percentage > 0) return "bg-red-400";
  return "bg-gray-200";
}

export function CohortRetentionMatrix({ data }: CohortRetentionMatrixProps) {
  const { t } = useTranslation("reports");

  // Translate cohort week helper - handles "Week of MMM DD, YYYY" format
  const translateCohortWeek = (cohortWeek: string) => {
    // Check if it matches "Week of ..." pattern
    const weekOfMatch = cohortWeek.match(/^Week of (.+)$/);
    if (weekOfMatch) {
      return `${t("charts.cohortRetention.weekOf")} ${weekOfMatch[1]}`;
    }
    return cohortWeek;
  };

  // If data is null (basic tier), show locked version
  if (data === null) {
    const mockCohorts = [
      { week: "Week 1", size: 45, retention: [100, 42, 38, 35, 33, 31, 29, 27] },
      { week: "Week 2", size: 52, retention: [100, 45, 40, 37, 35, 33, 31, 0] },
      { week: "Week 3", size: 38, retention: [100, 48, 43, 40, 38, 36, 0, 0] },
      { week: "Week 4", size: 61, retention: [100, 50, 45, 42, 40, 0, 0, 0] },
    ];

    const mockContent = (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left p-2 font-medium sticky left-0 bg-background z-10">
                {t("charts.cohortRetention.cohort")}
              </th>
              <th className="text-center p-2 font-medium">{t("charts.cohortRetention.size")}</th>
              {Array.from({ length: 8 }, (_, i) => (
                <th key={`week-header-${i}`} className="text-center p-2 font-medium">
                  W{i}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mockCohorts.map((cohort, _cohortIndex) => (
              <tr key={cohort.week}>
                <td className="p-2 font-medium whitespace-nowrap sticky left-0 bg-background z-10">
                  {cohort.week}
                </td>
                <td className="text-center p-2 text-muted-foreground">{cohort.size}</td>
                {cohort.retention.map((percentage, weekNum) => (
                  <td key={`week-${weekNum}`} className="p-2">
                    {percentage > 0 ? (
                      <div
                        className={`h-8 rounded flex items-center justify-center font-medium text-white ${getRetentionColor(percentage)}`}
                      >
                        {percentage}%
                      </div>
                    ) : (
                      <div className="h-8 bg-gray-100 rounded" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 text-xs flex-wrap">
          <span className="font-medium">{t("charts.cohortRetention.retention")}:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span>60%+</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-400" />
            <span>40-60%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-400" />
            <span>25-40%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-orange-400" />
            <span>15-25%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-400" />
            <span>&lt;15%</span>
          </div>
        </div>
      </div>
    );

    return (
      <LockedChartCard
        title={t("charts.cohortRetention.title")}
        tooltip={t("charts.cohortRetention.tooltip")}
        mockContent={mockContent}
        height="auto"
      />
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("charts.cohortRetention.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("charts.cohortRetention.noCohortData")}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Determine max weeks to display (show only weeks that have data)
  const maxWeeks = Math.min(12, Math.max(...data.map((cohort) => cohort.weeklyRetention.length)));

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
          {t("charts.cohortRetention.title")}
          <MetricTooltip content={t("charts.cohortRetention.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">{t("charts.cohortRetention.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left p-2 font-medium sticky left-0 bg-background z-10">
                  {t("charts.cohortRetention.cohort")}
                </th>
                <th className="text-center p-2 font-medium">{t("charts.cohortRetention.size")}</th>
                {Array.from({ length: maxWeeks }, (_, i) => (
                  <th key={`week-header-${i}`} className="text-center p-2 font-medium">
                    W{i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((cohort, cohortIndex) => (
                <tr
                  key={cohort.cohortWeek}
                  className="animate-in fade-in slide-in-from-left-4 duration-500"
                  style={{ animationDelay: `${cohortIndex * 50}ms` }}
                >
                  <td className="p-2 font-medium whitespace-nowrap sticky left-0 bg-background z-10">
                    {translateCohortWeek(cohort.cohortWeek)}
                  </td>
                  <td className="text-center p-2 text-muted-foreground">{cohort.cohortSize}</td>
                  {Array.from({ length: maxWeeks }, (_, weekNum) => {
                    const weekData = cohort.weeklyRetention.find((w) => w.weekNumber === weekNum);
                    if (!weekData || weekNum >= cohort.weeklyRetention.length) {
                      return (
                        <td key={`empty-week-${weekNum}`} className="p-2">
                          <div className="h-8 bg-gray-100 rounded" />
                        </td>
                      );
                    }

                    const percentage = weekData.retentionPercentage;
                    const colorClass = getRetentionColor(percentage);

                    return (
                      <td key={`data-week-${weekNum}`} className="p-2">
                        <div
                          className={`h-8 rounded flex items-center justify-center font-medium text-white transition-all duration-500 hover:scale-110 cursor-pointer ${colorClass}`}
                          title={`${t("charts.cohortRetention.week")} ${weekNum}: ${weekData.customersReturned}/${cohort.cohortSize} (${percentage.toFixed(1)}%)`}
                        >
                          {percentage > 0 ? `${percentage.toFixed(0)}%` : "-"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 text-xs flex-wrap">
          <span className="font-medium">{t("charts.cohortRetention.retention")}:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span>60%+</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-400" />
            <span>40-60%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-yellow-400" />
            <span>25-40%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-orange-400" />
            <span>15-25%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-400" />
            <span>&lt;15%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
